import React, { useContext, useMemo } from "react";
import ReactDOM from "react-dom";
import Dropdown from "components/Elements/Dropdown";
import ViewQueryButton from "components/QueryBuilder/ViewQueryButton";
import { QueryBuilderContext } from "contexts/QueryBuilderContextProvider";
import { BASEURL } from "components/Utils/Constants";
import {
  environment,
  hasQuery,
  QueryFiltersFromURL,
  querySegmentTracking,
  showHiddenMetrics,
  showHiddenDimensions,
} from "../Utils/QueryBuilderUtils";
import { truncate } from "components/Utils/Utils";
import * as Sentry from "@sentry/browser";
import FileSaver from "file-saver";
import { saveSvg, saveSvgAsPng } from "save-svg-as-png";
import moment from "moment";
import * as api from "./../../../api";
import { AppContext } from "contexts/AppContextProvider";

export const exportReportEnum = {
  TABLE: 1,
  LINE: 2,
  BAR: 3,
  STACKED_BAR: 4,
};

/**
 * Returns the configuration of the available options to export the current report
 * @param {*} option
 */
export const getExportReportConfig = (option) => {
  switch (option) {
    case exportReportEnum.TABLE:
      return {
        options: ["CSV"],
      };
    case exportReportEnum.LINE:
      return {
        options: ["CSV", "SVG", "PNG"],
      };
    case exportReportEnum.BAR:
      return {
        options: ["CSV", "SVG", "PNG"],
      };
    case exportReportEnum.STACKED_BAR:
      return {
        options: ["CSV", "SVG", "PNG"],
      };

    default:
      return {};
  }
};

const QueryExportData = (props) => {
  const { currentProject } = useContext(AppContext).state;
  const { state } = useContext(QueryBuilderContext);
  const { data, refReportElement } = state;
  let showLineChart = hasQuery(state) && state.query.chartType === "line-chart";
  let showSegments = hasQuery(state) && state.query.chartType === "segments";
  let showBarChart = hasQuery(state) && state.query.chartType === "bar-chart";
  let showStackedBarChart =
    hasQuery(state) && state.query.chartType === "bar-chart-stacked";
  let showTable = hasQuery(state) && state.query.chartType === "table";
  let showPivotTable =
    hasQuery(state) && state.query.chartType === "pivot-table";

  // Handle Pasted URL
  let pastedQueryFilters = useMemo(() => {
    QueryFiltersFromURL(
      state.queryStructure ? JSON.parse(state.queryStructure) : []
    );
  }, [state.queryStructure]);

  const startDate = moment(state.query.startDate).format("YYYY-MM-DD");
  const endDate = moment(state.query.endDate).format("YYYY-MM-DD");
  const friendlyDateName = props.dateRanges.find(
    (date) => startDate === date.from && endDate === date.to
  );

  let currentDate =
    moment(state.query.startDate).format("MM/DD/YY") +
    "-" +
    moment(state.query.endDate).format("MM/DD/YY");
  if (friendlyDateName && friendlyDateName.name !== undefined) {
    currentDate = friendlyDateName.name;
  }

  const downloadCSV = (fileName) => {
    const token = window.localStorage.token;
    const tenantID = localStorage.defaultTenantID;
    const projectID = currentProject.projectObj.id;
    // Overwrite default query limit
    state.query.limitSpec.limit = 5000;

    api
      .post(
        `${BASEURL}/query-builder?tenant_id=${tenantID}&project_id=${projectID}&env=${environment()}&hiddenMetrics=${showHiddenMetrics}&hiddenDimensions=${showHiddenDimensions}&format=csv`,
        { token, data: { query: state.query } }
      )
      .then((response) => response.text())
      .then((data) => {
        const blob = new Blob([data], { type: "text/csv;charset=utf-8" });
        FileSaver.saveAs(blob, `${fileName}.csv`);
      })

      .catch((error) => {
        Sentry.captureException(new Error("Query Builder CSV Export Error"), {
          tags: {
            section: "QueryBuilderCSVExport",
            error: error,
          },
        });
      });
  };

  const downloadSVG = (element, fileName) => {
    if (!element) return;

    let svg = null;
    try {
      svg = ReactDOM.findDOMNode(element.current).getElementsByClassName(
        "recharts-surface"
      )[0];
    } catch (err) {}

    if (svg) saveSvg(svg, `${fileName}.svg`);
  };

  const downloadPNG = (element, fileName) => {
    if (!element) return;

    let svg = null;
    try {
      svg = ReactDOM.findDOMNode(element.current).getElementsByClassName(
        "recharts-surface"
      )[0];
      svg.style = "background-color: #ffffff";
    } catch (err) {}

    if (svg) saveSvgAsPng(svg, `${fileName}.png`);
  };

  const handleExport = (type) => {
    const fileName = truncate(state.queryTextVersion.replace(/\s+/g, "_"), 100);

    switch (type) {
      case "CSV":
        downloadCSV(fileName);
        break;

      case "SVG":
        downloadSVG(refReportElement, fileName);
        break;

      case "PNG":
        downloadPNG(refReportElement, fileName);
        break;
      default:
        break;
    }

    querySegmentTracking(
      "csv-export",
      state,
      data,
      pastedQueryFilters,
      currentDate,
      exportConfig().options,
      localStorage.defaultTenantID,
      currentProject
    );
  };

  const exportConfig = () => {
    if (showSegments || showTable || showPivotTable) {
      return getExportReportConfig(exportReportEnum.TABLE);
    }
    if (showLineChart) {
      return getExportReportConfig(exportReportEnum.LINE);
    }
    if (showBarChart) {
      return getExportReportConfig(exportReportEnum.BAR);
    }
    if (showStackedBarChart) {
      return getExportReportConfig(exportReportEnum.STACKED_BAR);
    }
    return [];
  };

  return (
    <Dropdown
      toggle={(open, setOpen) => (
        <ViewQueryButton
          text="Export"
          icon="fas fa-cloud-download-alt"
          disabled={
            !(
              Array.isArray(exportConfig().options) &&
              exportConfig().options.length > 0
            )
          }
          labelWrapperClass={
            (Array.isArray(exportConfig().options) &&
            exportConfig().options.length > 0
              ? "query-option-button--strong-label "
              : "") + (open ? "query-active-button" : " ")
          }
          iconWrapperClass={"ml-2 query-option-button--strong-label"}
          handleToggle={() => setOpen(!open)}
          border={true}
        />
      )}
      onChange={(value) => handleExport(value)}
      options={exportConfig().options}
    />
  );
};

export default QueryExportData;
